import { Grid, Typography } from "@mui/material";
import { ChartData, Chart as ChartJS, ChartOptions } from "chart.js";
import {
  CategoryScale,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Plan, Profile, WorkoutCategories } from "@readyfit-common/models";
import {
  convertMinutesToHours,
  convertSecondsToHours,
} from "@readyfit-common/utils";
import useStyles from "./PlanOverviewGraphs.style";
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

interface PlanOverviewGraphsProps {
  athlete: Profile;
  plan: Plan;
}

const CustomLegend = () => {
  return (
    <Grid
      container
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
      }}
    >
      <Grid
        item
        style={{
          display: "flex",
          alignItems: "center",
          width: "110px",
        }}
      >
        <div
          style={{
            width: "16px",
            height: "4px",
            backgroundColor: "#59806D",
            marginRight: "8px",
            flexShrink: 0,
          }}
        />
        <Typography variant="subtitle1" noWrap>
          Completed
        </Typography>
      </Grid>
      <Grid
        item
        style={{
          display: "flex",
          alignItems: "center",
          width: "110px",
        }}
      >
        <div
          style={{
            width: "16px",
            height: "4px",
            backgroundColor: "#A8A8A8",
            marginRight: "8px",
            flexShrink: 0,
          }}
        />
        <Typography variant="subtitle1" noWrap>
          Assigned
        </Typography>
      </Grid>
    </Grid>
  );
};

const PlanOverviewGraphs = ({ athlete, plan }: PlanOverviewGraphsProps) => {
  const classes = useStyles();
  const weeks = plan.phases[0]?.weeks || [];

  const totalMilesPerWeek = weeks.map((week) =>
    week.workouts
      .filter((workout) => workout.workoutCategoryId === WorkoutCategories.RUN)
      .reduce(
        (total, workout) => total + (Number(workout.completedDistance) || 0),
        0,
      ),
  );
  const totalAssignedMilesPerWeek = weeks.map((week) =>
    week.workouts
      .filter((workout) => workout.workoutCategoryId === WorkoutCategories.RUN)
      .reduce((total, workout) => total + (Number(workout.distance) || 0), 0),
  );

  const totalAssignedYardsPerWeek = weeks.map((week) =>
    week.workouts
      .filter((workout) => workout.workoutCategoryId === WorkoutCategories.SWIM)
      .reduce((total, workout) => total + (Number(workout.distance) || 0), 0),
  );
  const totalYardsPerWeek = weeks.map((week) =>
    week.workouts
      .filter((workout) => workout.workoutCategoryId === WorkoutCategories.SWIM)
      .reduce(
        (total, workout) => total + (Number(workout.completedDistance) || 0),
        0,
      ),
  );

  const totalAssignedHoursPerWeek = weeks
    .map((week) =>
      week.workouts
        .filter(
          (workout) => workout.workoutCategoryId === WorkoutCategories.FITNESS,
        )
        .reduce(
          (total, workout) => total + (Number(workout.estimatedTime) || 0),
          0,
        ),
    )
    .map((minutes) => Number(convertMinutesToHours(minutes)));

  const totalHoursPerWeek = weeks
    .map((week) =>
      week.workouts
        .filter(
          (workout) => workout.workoutCategoryId === WorkoutCategories.FITNESS,
        )
        .reduce(
          (total, workout) => total + (Number(workout.completedTime) || 0),
          0,
        ),
    )
    .map((seconds) => Number(convertSecondsToHours(seconds)));

  const options: ChartOptions<"line"> = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        // position: "top" as const,
        // labels: {
        //   boxWidth: 20,
        //   padding: 20,
        // },
      },
      title: {
        display: false, // Hide "Chart.js Line chart"
      },
    },
    scales: {
      x: {
        grid: {
          display: false, // remove X-axis grid lines
        },
        ticks: {
          font: {
            weight: "bold",
          },
        },
        beginAtZero: true,
      },
      y: {
        grid: {
          color: "#E0E0E0", // light gray Y grid lines
        },
        beginAtZero: true,
      },
    },
    elements: {
      point: {
        hitRadius: 10,
        hoverRadius: 5,
      },
      line: {
        tension: 0.6, // smoother bell curves
      },
    },
  };

  const labels = weeks.map((week) => `Week ${week.numberOfWeek}`);

  const runningData: ChartData<"line"> = {
    labels,
    datasets: [
      {
        label: "Completed",
        backgroundColor: "#59806D",
        borderColor: "#59806D",
        borderWidth: 2,
        data: totalMilesPerWeek,
        tension: 0.4,
      },
      {
        label: "Assigned",
        backgroundColor: "#A8A8A8",
        borderColor: "#A8A8A8",
        borderWidth: 2,
        data: totalAssignedMilesPerWeek,
        tension: 0.4,
        borderDash: [5, 5],
      },
    ],
  };

  const swimmingData: ChartData<"line"> = {
    labels,
    datasets: [
      {
        label: "Completed",
        backgroundColor: "#59806D",
        borderColor: "#59806D",
        borderWidth: 2,
        data: totalYardsPerWeek,
        tension: 0.4,
      },
      {
        label: "Assigned",
        backgroundColor: "#A8A8A8",
        borderColor: "#A8A8A8",
        borderWidth: 2,
        data: totalAssignedYardsPerWeek,
        tension: 0.4,
        borderDash: [5, 5],
      },
    ],
  };

  const fitnessData: ChartData<"line"> = {
    labels,
    datasets: [
      {
        label: "Completed",
        backgroundColor: "#59806D",
        borderColor: "#59806D",
        borderWidth: 2,
        data: totalHoursPerWeek,
        tension: 0.6,
      },
      {
        label: "Assigned",
        backgroundColor: "#A8A8A8",
        borderColor: "#A8A8A8",
        borderWidth: 2,
        data: totalAssignedHoursPerWeek,
        tension: 0.6,
        borderDash: [5, 5],
      },
    ],
  };

  const totalRunningMilesLabel = totalMilesPerWeek.reduce(
    (total, miles) => total + miles,
    0,
  );

  const totalRunningAssignedMilesLabel = totalAssignedMilesPerWeek.reduce(
    (total, miles) => total + miles,
    0,
  );

  const totalSwimmingYardsLabel = totalYardsPerWeek.reduce(
    (total, yards) => total + yards,
    0,
  );

  const totalSwimmingAssignedYardsLabel = totalAssignedYardsPerWeek.reduce(
    (total, yards) => total + yards,
    0,
  );

  const totalFitnessHoursLabel = totalHoursPerWeek.reduce(
    (total, hours) => total + hours,
    0,
  );

  const totalFitnessAssignedHoursLabel = totalAssignedHoursPerWeek.reduce(
    (total, hours) => total + hours,
    0,
  );

  return (
    <Grid container className={classes.container}>
      <Grid item xs={12}>
        <Typography variant="h5">Plan Overview</Typography>
      </Grid>

      <Grid container>
        <Grid container item xs={4} className={classes.graphContainer}>
          <Grid container rowGap={2}>
            <Grid item xs={12}>
              <Typography
                variant="h5"
                color={"#666"}
                textTransform={"uppercase"}
                fontSize={"18px"}
              >
                Running
              </Typography>
            </Grid>

            <Grid container item xs={12} flexDirection={"row"}>
              <Grid item xs={6}>
                <Typography variant="h5" fontSize={"18px"}>
                  {totalRunningMilesLabel} / {totalRunningAssignedMilesLabel}{" "}
                  miles
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <CustomLegend />
              </Grid>
            </Grid>
          </Grid>
          <Line options={options} data={runningData} />
        </Grid>
        <Grid container item xs={4} className={classes.graphContainer}>
          <Grid container xs={12}>
            <Typography
              variant="h5"
              color={"#666"}
              textTransform={"uppercase"}
              fontSize={"18px"}
            >
              Swimming
            </Typography>

            <Grid container item xs={12} flexDirection={"row"}>
              <Grid item xs={6}>
                <Typography variant="h5" fontSize={"18px"}>
                  {totalSwimmingYardsLabel} / {totalSwimmingAssignedYardsLabel}{" "}
                  yards
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <CustomLegend />
              </Grid>
            </Grid>
          </Grid>
          <Line options={options} data={swimmingData} />
        </Grid>
        <Grid container item xs={4} className={classes.graphContainer}>
          <Grid container xs={12}>
            <Typography
              variant="h5"
              color={"#666"}
              textTransform={"uppercase"}
              fontSize={"18px"}
            >
              Fitness
            </Typography>

            <Grid container item xs={12} flexDirection={"row"}>
              <Grid item xs={6}>
                <Typography variant="h5" fontSize={"18px"}>
                  {totalFitnessHoursLabel} / {totalFitnessAssignedHoursLabel}{" "}
                  hours
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <CustomLegend />
              </Grid>
            </Grid>
          </Grid>
          <Line options={options} data={fitnessData} />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PlanOverviewGraphs;
